import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import PageWrapper from "../../components/layout/PageWrapper";
import { getServiceById, servicesData } from "../../data/servicesData";
import { galleryCategories, serviceGalleryMap } from "../../data/galleryData";
import ScrollReveal from "../../components/animations/ScrollReveal";

const iconMap = {
  event: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  social: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  ),
  digital: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  construction: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = getServiceById(serviceId || "");

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!service) {
    return (
      <PageWrapper>
        <section className="py-24 text-center">
          <p className="font-sans text-aura-text">Service not found.</p>
          <Link to="/services" className="mt-4 inline-block text-gold-primary hover:underline">
            Back to Services
          </Link>
        </section>
      </PageWrapper>
    );
  }

  const publicUrl = process.env.PUBLIC_URL || "";
  const heroImagesByService = {
    "event-management": `${publicUrl}/Images/eventheorsection.jpg`,
    "social-digital": `${publicUrl}/Images/socialmediaherosection.jpg`,
    "construction-interior": `${publicUrl}/Images/interiourdesignherosection.jpg`,
    "fashion-beauty": `${publicUrl}/Images/fashionandbeautyherosection.jpeg`,
  };
  const heroImage = heroImagesByService[service.id] ?? `${publicUrl}/Images/herosectiondesktop.png`;
  const backgroundImage = `linear-gradient(120deg, rgba(7, 7, 10, 0.96), rgba(7, 7, 10, 0.35)), url('${heroImage}')`;

  let backgroundPosition = "center top";
  if (service.id === "fashion-beauty" && isMobile) {
    backgroundPosition = "25% center";
  }

  const audiencesByService = {
    "event-management": [
      "Corporate teams planning conferences, summits, or annual gatherings",
      "Brands launching new products or services that need a standout moment",
      "Luxury and premium businesses that want event experiences to reflect their positioning",
      "Government, semi‑government, or institutional clients that need reliable end‑to‑end coordination",
    ],
    "social-digital": [
      "Brands that need an always‑on social and digital presence, not one‑off posts",
      "Marketing teams that want clear, data‑driven reporting on what is working",
      "Businesses that rely on website traffic, leads, or online sales to grow",
      "Founders and decision‑makers who want one partner to manage both social and performance marketing",
    ],
    "construction-interior": [
      "Businesses fitting out new offices, retail spaces, or hospitality venues",
      "Owners who want one point of contact to coordinate contractors and approvals",
      "Teams that need spaces to be both operationally efficient and brand‑aligned",
      "Clients who value clear timelines, risk management, and quality control during build‑out",
    ],
    "fashion-beauty": [
      "Fashion labels preparing new collections or capsule drops",
      "Beauty brands launching products, ranges, or seasonal campaigns",
      "Retailers who want in‑store experiences and merchandising to feel premium",
      "Marketing teams looking for fresh creative, content, and collaborations that feel on‑trend",
    ],
  };

  const deliverablesByService = {
    "event-management": [
      "Concept decks, moodboards, and full event narrative",
      "Venue sourcing shortlists and vendor recommendations",
      "Run‑of‑show, technical riders, and logistics plans",
      "On‑site production management and guest‑flow coordination",
      "Post‑event reports with photos, highlights, and key learnings",
    ],
    "social-digital": [
      "Channel and content strategy with clear goals and KPIs",
      "Monthly content calendars and asset guidelines",
      "Media plans for paid social, search, and display",
      "Performance dashboards and insights reviews",
      "Testing roadmaps for creatives, audiences, and offers",
    ],
    "construction-interior": [
      "High‑level space plans and layout recommendations",
      "Bill of quantities support and vendor/contractor briefs",
      "Site visit reports and progress updates",
      "Material, fixture, and finish schedules aligned to your brand",
      "Snag lists, handover documentation, and post‑completion support",
    ],
    "fashion-beauty": [
      "Brand or campaign creative direction decks",
      "Lookbooks, campaign concepts, and shot lists",
      "Launch event or in‑store activation plans",
      "Social and ecommerce content packs",
      "Influencer, PR, and partner activation outlines",
    ],
  };

  const audiences = audiencesByService[service.id] || [];
  const deliverables = deliverablesByService[service.id] || [];
  const isEventService = service.id === "event-management";

  const galleryCategoryIds = serviceGalleryMap[service.id] || [];
  const serviceGalleryImages = galleryCategoryIds.flatMap((categoryId) => {
    const category = galleryCategories.find((c) => c.id === categoryId);
    return category ? category.images : [];
  });

  const icon = iconMap[service.icon] || iconMap.event;

  return (
    <>
      {/* Hero section — full viewport, refined for all services */}
      <section
        className="relative flex items-center justify-center overflow-hidden py-16 md:py-20"
        style={{
          minHeight: "70vh",
          backgroundImage,
          backgroundSize: "cover",
          backgroundPosition,
          backgroundRepeat: "no-repeat",
          backgroundColor: "#07070a",
        }}
      >
        <div className="absolute inset-0 bg-black/40" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#07070a] via-transparent pointer-events-none" />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 mb-4 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-primary" />
              <span className="font-sans text-[11px] tracking-[0.24em] uppercase text-white/80">
                {service.title}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-sm bg-white/15 flex items-center justify-center text-white">
                {icon}
              </div>
              <span className="font-sans text-xs uppercase tracking-[0.28em] text-white/70">
                Services · Aura International
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-[3rem] font-semibold text-white leading-tight">
              {isEventService
                ? "Event management that feels composed, not chaotic."
                : service.title}
            </h1>
            <p className="mt-4 font-sans text-base md:text-lg text-white/85 max-w-2xl">
              {service.description}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                to="/contact"
                variant="primary"
                className="!bg-gold-primary !text-aura-text !border-gold-primary hover:!bg-white hover:!border-white"
              >
                {isEventService ? "Discuss your next event" : "Talk to our team"}
              </Button>
              <Button
                to={isEventService ? "#event-overview" : "/services"}
                variant="outline"
                className="!bg-transparent !text-white !border-2 !border-white/70 hover:!bg-white hover:!text-aura-text"
              >
                {isEventService ? "Explore how this works" : "All services"}
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs font-sans text-white/75">
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span>End‑to‑end support</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                <span>Built around your brand and goals</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <PageWrapper>
      {/* Overview + quick facts */}
      <section
        id={isEventService ? "event-overview" : undefined}
        className="py-16 md:py-20 bg-aura-bg"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <div className="grid lg:grid-cols-[minmax(0,1.6fr),minmax(0,1.1fr)] gap-10 lg:gap-14 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-8 w-px bg-gold-primary" />
                  <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-gold-dark">
                    Service overview
                  </span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4">
                  What this service helps you achieve
                </h2>
                <p className="font-sans text-aura-text/90 leading-relaxed">
                  {service.longDescription}
                </p>
              </div>

              <div className="rounded-xl border border-gold-muted/30 bg-white/60 shadow-sm backdrop-blur-sm p-6 space-y-5">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-sans text-[11px] uppercase tracking-[0.26em] text-gold-dark">
                    At a glance
                  </p>
                  <span className="inline-flex items-center gap-2 rounded-full bg-gold-light/40 px-3 py-1 text-[11px] font-medium text-aura-text border border-gold-muted/60">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold-primary" />
                    Tailored for decision‑makers
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm font-sans text-aura-text/90">
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-gold-dark/90">
                      Ideal for
                    </p>
                    <p>
                      {isEventService
                        ? "Marketing, corporate communications, HR, and leadership teams."
                        : "Brands and teams who want joined‑up, expert support."}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-gold-dark/90">
                      Typical engagement
                    </p>
                    <p>
                      {isEventService
                        ? "From one‑off flagship moments to annual event calendars."
                        : "Project‑based or retainer support depending on scope."}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-gold-dark/90">
                      Our role
                    </p>
                    <p>
                      {isEventService
                        ? "Single point of coordination across venues, vendors, and run‑of‑show."
                        : "Strategy, creative direction, and hands‑on execution."}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-gold-dark/90">
                      Outcome
                    </p>
                    <p>
                      {isEventService
                        ? "Events that feel on‑brand, controlled, and memorable—for all the right reasons."
                        : "Clearer, more consistent brand experiences that move your metrics."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Event-specific deep dive — only for Event Management */}
      {isEventService && (
        <>
          {/* What we manage */}
          <section className="py-16 md:py-20 bg-gold-light/10 border-y border-gold-muted/10">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <ScrollReveal delay={0.1}>
                <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10 lg:items-center">
                  <div>
                    <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4">
                      End‑to‑end Event Management & Organization
                    </h2>
                    <p className="font-sans text-aura-text/90 leading-relaxed mb-4">
                      Whether you&apos;re hosting a 100‑person leadership offsite or a multi‑day conference,
                      we design and manage every detail so the experience feels effortless for your guests
                      and your team. From first save‑the‑date to the final thank‑you email, we keep
                      logistics, partners, and production moving in sync.
                    </p>
                    <p className="font-sans text-aura-text/90 leading-relaxed">
                      Our role is to translate your objectives into an experience: what guests see, hear,
                      and feel at every touchpoint. You stay focused on content and relationships, while we
                      take care of venues, timelines, staging, and all the &quot;what if&quot; scenarios in the
                      background.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { label: "Events delivered", value: "150+ " },
                      { label: "Cities covered", value: "20+ " },
                      { label: "Average lead time", value: "6–12 weeks" },
                      { label: "Client satisfaction", value: "95%+" },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-sm border border-gold-muted/30 bg-white/50 px-4 py-4 shadow-sm backdrop-blur-sm"
                      >
                        <p className="font-sans text-xs uppercase tracking-[0.2em] text-gold-dark mb-1">
                          {item.label}
                        </p>
                        <p className="font-serif text-2xl font-semibold text-aura-text">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </section>

          {/* Types of events */}
          <section className="py-16 md:py-20 bg-gradient-to-b from-aura-bg via-aura-text to-aura-text">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <ScrollReveal delay={0.1}>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                  <div>
                    <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-3">
                      Events we specialise in
                    </h2>
                    <p className="font-sans text-aura-text/90 max-w-2xl">
                      From strategic business forums to immersive brand activations, we curate experiences
                      that feel premium, considered, and completely on‑brand.
                    </p>
                  </div>
                  <p className="font-sans text-sm text-aura-text/70 max-w-xs">
                    Not sure where your event fits? Share your objectives and audience, and we&apos;ll
                    recommend the right format, scale, and program structure.
                  </p>
                </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    {
                      title: "Corporate Conferences & Summits",
                      text: "Multi‑session programs with plenaries, breakouts, and experiences that keep delegates engaged over one or more days.",
                    },
                    {
                      title: "Product Launches & Brand Activations",
                      text: "High‑impact moments that introduce, demonstrate, or celebrate your brand in a way guests remember and talk about.",
                    },
                    {
                      title: "Leadership & Partner Gatherings",
                      text: "Board meetings, investor days, and leadership offsites designed for meaningful conversation and connection.",
                    },
                    {
                      title: "Internal Culture & Employee Events",
                      text: "Town halls, milestone celebrations, and internal campaigns that bring teams together and reinforce your culture.",
                    },
                    {
                      title: "Public & Community Experiences",
                      text: "Open‑to‑public events in malls, outdoor spaces, and venues that need clear crowd management and safety planning.",
                    },
                    {
                      title: "Hybrid & Experiential Formats",
                      text: "Events that blend in‑person and digital touchpoints—live streams, content hubs, and on‑demand recaps.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="relative h-full rounded-2xl border border-gold-light/40 bg-gradient-to-b from-aura-text via-black to-black px-5 py-6 shadow-md"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-primary via-gold-light to-gold-primary/70" />
                      <h3 className="font-serif text-lg font-semibold text-white mb-2 mt-3">
                        {item.title}
                      </h3>
                      <p className="font-sans text-sm text-gold-light/90 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </section>

          {/* Event journey */}
          <section className="py-16 md:py-20 bg-gold-light/10 border-y border-gold-muted/10">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <ScrollReveal delay={0.1}>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4 text-center">
                  A clear journey from brief to applause
                </h2>
                <p className="font-sans text-aura-text/85 text-center max-w-2xl mx-auto mb-10">
                  We use a structured process so you always know what&apos;s happening next, who&apos;s
                  responsible, and how decisions move the event forward.
                </p>
                <div className="grid md:grid-cols-4 gap-6">
                  {[
                    {
                      step: "01",
                      title: "Discovery",
                      text: "Clarify goals, success metrics, audience profile, and budget so we can recommend the right approach.",
                    },
                    {
                      step: "02",
                      title: "Concept & Planning",
                      text: "Develop event concept, program, and environment, then lock in venues, partners, and timelines.",
                    },
                    {
                      step: "03",
                      title: "Production",
                      text: "Coordinate vendors, rehearsals, and on‑site build so the experience feels seamless on the day.",
                    },
                    {
                      step: "04",
                      title: "Show Day & Beyond",
                      text: "Run the event, manage live moments, and capture content, learnings, and feedback for next time.",
                    },
                  ].map((item) => (
                    <div key={item.step} className="relative">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold-primary text-aura-text font-serif text-sm font-semibold">
                          {item.step}
                        </span>
                        <h3 className="font-serif text-base md:text-lg font-semibold text-aura-text">
                          {item.title}
                        </h3>
                      </div>
                      <p className="font-sans text-sm text-aura-text/90 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </section>
        </>
      )}

      {/* Who this is for */}
      {audiences.length > 0 && (
        <section className="py-16 md:py-20 bg-gradient-to-b from-gold-light/15 via-aura-bg to-aura-bg border-y border-gold-muted/20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={0.1}>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-3">
                    Who this service is for
                  </h2>
                  <p className="font-sans text-aura-text/90 max-w-2xl">
                    This service is designed for teams and brands who recognise the value of doing things properly and want a partner that thinks beyond a single campaign or project.
                  </p>
                </div>
                <div className="rounded-xl border border-gold-muted/40 bg-gold-light/40 px-4 py-3 max-w-sm">
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-gold-dark mb-1">
                    Typical stakeholders
                  </p>
                  <p className="font-sans text-sm text-aura-text/90">
                    {isEventService
                      ? "Marketing, communications, HR, and leadership teams shaping key moments in the calendar."
                      : "Founders, marketers, and operators looking for a dependable strategic partner."}
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5">
                {audiences.map((item, index) => (
                  <div
                    key={index}
                    className="relative overflow-hidden rounded-xl border border-gold-muted/30 bg-white/70 px-5 py-5 shadow-sm backdrop-blur-sm"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-primary via-gold-dark/80 to-gold-light/80" />
                    <div className="mt-2 flex items-start gap-3">
                      <span className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gold-primary/15 text-gold-dark text-xs font-semibold">
                        {index + 1}
                      </span>
                      <p className="font-sans text-sm text-aura-text/95 leading-relaxed">
                        {item}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* What we offer */}
      {service.highlights && service.highlights.length > 0 && (
        <section className="py-16 md:py-20 bg-gradient-to-b from-aura-text via-aura-text to-aura-bg border-y border-gold-muted/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={0.1}>
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="inline-flex items-center justify-center rounded-full border border-gold-light/60 bg-white/10 px-4 py-1 text-[11px] font-sans uppercase tracking-[0.24em] text-gold-light mb-3">
                  Core capabilities
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-white mb-2">
                  What we offer within this service
                </h2>
                <p className="font-sans text-sm md:text-base text-gold-light/90">
                  A combination of planning, creativity, and precise delivery that adapts to the shape of your brief.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.highlights.map((item, i) => (
                  <div
                    key={i}
                    className="relative h-full rounded-xl border border-gold-light/40 bg-black/35 px-5 py-6 shadow-md backdrop-blur-sm hover:border-gold-primary/80 hover:bg-black/55 transition-all duration-300"
                  >
                    <div className="absolute right-4 top-4 h-8 w-8 rounded-full bg-gold-primary/15 flex items-center justify-center text-[11px] font-semibold text-gold-dark">
                      {i + 1}
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-gold-primary text-aura-text text-sm font-semibold">
                        ✓
                      </span>
                      <p className="font-sans text-sm text-gold-light/95 leading-relaxed">
                        {item}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* What you can expect to receive */}
      {deliverables.length > 0 && (
        <section className="py-16 md:py-20 bg-aura-bg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={0.1}>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4 text-center">
                What you can expect to receive
              </h2>
              <p className="font-sans text-aura-text/90 text-center max-w-2xl mx-auto mb-10">
                Every engagement is customised, but most projects include a mix of strategic thinking, clear documentation, and hands‑on execution like the examples below.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {deliverables.map((item, index) => (
                  <div
                    key={index}
                    className="relative h-full overflow-hidden rounded-xl border border-gold-muted/40 bg-gradient-to-b from-white via-white to-gold-light/40 px-5 py-6 shadow-sm"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-primary via-gold-dark/80 to-gold-light/80" />
                    <h3 className="font-serif text-base md:text-lg font-semibold text-aura-text mb-2 mt-3">
                      Deliverable {index + 1}
                    </h3>
                    <p className="font-sans text-sm text-aura-text/90 leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Gallery for this service */}
      {serviceGalleryImages.length > 0 && (
        <section className="py-16 md:py-20 bg-gradient-to-b from-aura-bg via-aura-text to-aura-text">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={0.1}>
              <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
                <span className="block font-sans text-xs md:text-sm font-medium uppercase tracking-[0.25em] text-gold-light mb-3">
                  Gallery
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-white">
                  Recent work in {service.title}
                </h2>
                <p className="mt-4 font-sans text-sm md:text-base text-gold-light/85">
                  A closer look at the type of environments, experiences, and moments we create in this area of our work.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {serviceGalleryImages.map((image, index) => (
                  <motion.div
                    key={`${image.src}-${index}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group relative overflow-hidden rounded-xl border border-gold-light/20 bg-gold-light/10"
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Why choose this service — card style */}
      {service.benefits && service.benefits.length > 0 && (
        <section className="py-16 md:py-20 bg-gradient-to-b from-aura-text via-aura-text to-aura-bg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={0.1}>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-gold-primary mb-4 text-center">
                Why choose this service
              </h2>
              <p className="font-sans text-gold-light/85 text-center max-w-2xl mx-auto mb-12">
                What you gain when you work with us
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.benefits.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="group flex flex-col gap-3 p-6 rounded-xl border border-gold-primary/40 bg-gradient-to-b from-gold-dark/25 via-gold-dark/15 to-transparent hover:from-gold-primary/25 hover:border-gold-primary/70 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-gold-primary flex items-center justify-center text-gold-primary font-serif text-base font-semibold group-hover:bg-gold-primary group-hover:text-aura-text transition-colors duration-300">
                        {i + 1}
                      </span>
                      <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold-light/80">
                        Key advantage
                      </span>
                    </div>
                    <p className="font-sans text-sm text-gold-light/95 leading-relaxed">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Our process */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gold-light/18 via-aura-bg to-aura-bg border-y border-gold-muted/25">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-3">
                  How we work with you
                </h2>
                <p className="font-sans text-aura-text/90 max-w-2xl">
                  A clear, step‑by‑step process so you always know what is happening, what is next, and who is responsible.
                </p>
              </div>
              <div className="rounded-full border border-gold-muted/60 bg-white/70 px-5 py-2 text-xs font-sans text-aura-text/90">
                From first conversation to final handover, we stay close to detail so you can stay close to your stakeholders.
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {(isEventService
                ? [
                    {
                      step: "1",
                      title: "Brief & Alignment",
                      text: "We translate your objectives, audience, and constraints into a clear event mandate we can plan against.",
                    },
                    {
                      step: "2",
                      title: "Design & Run‑of‑Show",
                      text: "We shape the concept, agenda, guest journey, and production plan so every moment has a purpose.",
                    },
                    {
                      step: "3",
                      title: "Pre‑Event Coordination",
                      text: "We manage vendors, invites, staging, rehearsals, and contingency planning so show day runs smoothly.",
                    },
                    {
                      step: "4",
                      title: "On‑site Management & Wrap‑up",
                      text: "We direct the event live, then capture performance, learnings, and content for your next communication.",
                    },
                  ]
                : [
                    {
                      step: "1",
                      title: "Discover",
                      text: "We learn your goals, audience, and requirements so our approach is tailored to you.",
                    },
                    {
                      step: "2",
                      title: "Plan",
                      text: "We propose a clear strategy, timeline, and deliverables with your sign-off before execution.",
                    },
                    {
                      step: "3",
                      title: "Execute",
                      text: "We deliver with precision and keep you updated at every stage so nothing is left to chance.",
                    },
                    {
                      step: "4",
                      title: "Optimize",
                      text: "We measure results, gather feedback, and refine so you get lasting value.",
                    },
                  ]
              ).map((item) => (
                <div
                  key={item.step}
                  className="relative rounded-xl border border-gold-muted/50 bg-white/80 px-4 py-5 shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gold-primary text-aura-text text-xs font-semibold">
                      {item.step}
                    </span>
                    <h3 className="font-serif text-base md:text-lg font-semibold text-aura-text">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-1 font-sans text-sm text-aura-text/90 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 md:py-20 bg-aura-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text">
                  Other services you may need
                </h2>
                <p className="mt-3 font-sans text-aura-text/90 max-w-2xl">
                  Explore our full range of solutions that often sit alongside this service.
                </p>
              </div>
              <Button to="/services" variant="outline">
                View all services
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {servicesData
                .filter((s) => s.id !== service.id)
                .map((s) => (
                  <Link
                    key={s.id}
                    to={`/services/${s.id}`}
                    className="group relative block h-full rounded-xl border border-gold-muted/30 bg-white/80 p-5 shadow-sm hover:border-gold-primary/70 hover:shadow-md transition-all duration-300"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-primary via-gold-dark/80 to-gold-light/80 opacity-80 group-hover:opacity-100" />
                    <h3 className="mt-3 font-serif text-lg font-semibold text-aura-text group-hover:text-gold-primary transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-2 font-sans text-sm text-aura-text/90 line-clamp-3">
                      {s.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 font-sans text-sm font-medium text-gold-primary group-hover:underline">
                      View details
                      <span>→</span>
                    </span>
                  </Link>
                ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-gold-dark via-gold-primary to-gold-light/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal delay={0.1}>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4">
              Ready to get started?
            </h2>
            <p className="font-sans text-aura-text/90 mb-8 max-w-2xl mx-auto">
              Discuss your project or learn more about how we can help with {service.title.toLowerCase()}.
            </p>
            <Button to="/contact" variant="primary" className="!bg-aura-text !text-gold-primary hover:!bg-black hover:!text-gold-light">
              Contact Us
            </Button>
          </ScrollReveal>
        </div>
      </section>
      </PageWrapper>
    </>
  );
}
